import {HttpErrorResponse} from "@angular/common/http";
import {Injectable} from "@angular/core";
import {TranslateService} from "@ngx-translate/core";
import {ToastrService} from "ngx-toastr";
import {HttpEventService} from "src/app/services/http/http-event.service";

@Injectable()
export class NotificationsService {

	constructor(
		private toastrService: ToastrService,
		private httpEventService: HttpEventService,
		private translateService: TranslateService) {
		this.listenToHttpServerErrors();
		this.listenToHttpClientErrors();
	}

	clearMessage() {
		this.toastrService.clear();
	}

	showSuccess(message: string) {
		this.toastrService.success("", message, {timeOut: 10000});
	}

	showInfo(message: string) {
		this.toastrService.info("", message);
	}

	showError(title: string, params?: any, message?: string) {
		if (!message) message = "";
		title = this.translateService.instant(title, params);
		this.toastrService.error(message, title, {timeOut: 10000});
	}

	private listenToHttpServerErrors() {
		this.httpEventService.onServerError.subscribe((e: HttpErrorResponse) => {
			e.status === 0 ? this.displayNetworkFailureMessage(e) : this.displayServerErrorMessage(e);
		});
	}

	private displayNetworkFailureMessage(e: HttpErrorResponse) {
		const message = e.statusText;
		const title = this.translateService.instant("common.ERROR_ID", {errorId: e.status});
	}

	private displayServerErrorMessage(e: HttpErrorResponse) {
		const message = e.message;
		const title = this.translateService.instant("common.ERROR_ID", {errorId: e.status});
		this.showError(title, message);
	}


	private listenToHttpClientErrors() {
		this.httpEventService.onNameExistError.subscribe((e: any) => {
			const title = this.translateService.instant("common.ALREADY_EXIST", {entity: e});
			this.showError(title, "");
		});

		this.httpEventService.onTenantMappingExistError.subscribe((e: any) => {
			const title = this.translateService.instant("common.TENANT_MAPPED");
			this.showError(title, "");
		});

		this.httpEventService.onInterfaceCodeExistError.subscribe((e: any) => {
			const title = this.translateService.instant("common.ALREADY_EXIST", {entity: e});
			this.showError(title, "");
		});

		this.httpEventService.onFileDownloadError.subscribe((fileName: any) => {
			const title = this.translateService.instant("fileManagement.retrieve.ERROR_FILE_DOWNLOAD", {fileName: fileName});
			this.showError(title, "");
		});

		this.httpEventService.onFileUploadError.subscribe((fileName: any) => {
			const title = this.translateService.instant("fileManagement.send.FILE_UPLOADED_FAILED", {fileName: fileName});
			this.showError(title, "");
		});

		this.httpEventService.onDuplicateFileContentError.subscribe((errorMessage: any) => {
			this.showError(errorMessage, "");
		});

		this.httpEventService.onFileAlreadyRegisteredError.subscribe((errorMessage: any) => {
			this.showError(errorMessage, "");
		});

		this.httpEventService.onFileNameInvalidError.subscribe((fileName: any) => {
			const title = this.translateService.instant("fileManagement.send.FILE_NAME_INVALID", {fileName: fileName});
			this.showError(title, "");
		});

		this.httpEventService.onDocumentTypeInactiveError.subscribe((fileName: any) => {
			const title = this.translateService.instant("fileManagement.send.DOCUMENT_TYPE_INACTIVE", {fileName: fileName});
			this.showError(title, "");
		});

		this.httpEventService.onDocumentTypeInvalidError.subscribe((fileName: any) => {
			const title = this.translateService.instant("fileManagement.send.DOCUMENT_TYPE_INVALID", {fileName: fileName});
			this.showError(title, "");
		});

		this.httpEventService.onClientRelationshipInactiveError.subscribe((fileName: any) => {
			const title = this.translateService.instant("fileManagement.common.CLIENT_RELATIONSHIP_INACTIVE", {fileName: fileName});
			this.showError(title, "");
		});

		this.httpEventService.onClientRelationshipInvalidError.subscribe((fileName: any) => {
			const title = this.translateService.instant("fileManagement.common.CLIENT_RELATIONSHIP_INVALID", {fileName: fileName});
			this.showError(title, "");
		});

		this.httpEventService.onInactiveRecipientError.subscribe((fileName: any) => {
			const title = this.translateService.instant("fileManagement.send.INACTIVE_RECIPINET", {fileName: fileName});
			this.showError(title, "");
		});

		this.httpEventService.onZeroByteFileError.subscribe((fileName: any) => {
			const title = this.translateService.instant("fileManagement.send.ZERO_BYTE_FILE_ERROR", {fileName: fileName});
			this.showError(title, "");
		});

		this.httpEventService.onInvalidRecipientShortNameError.subscribe((fileName: any) => {
			const title = this.translateService.instant("fileManagement.common.INVALID_RECIPIENT_SHORT_NAME", {fileName: fileName});
			this.showError(title, "");
		});

		this.httpEventService.onInactiveSenderError.subscribe((fileName: any) => {
			const title = this.translateService.instant("fileManagement.common.INACTIVE_SENDER", {fileName: fileName});
			this.showError(title, "");
		});

		this.httpEventService.onExportedFileNotFoundError.subscribe((fileName: any) => {
			const title = this.translateService.instant("fileManagement.retrieve.EXPORTED_FILE_NOT_FOUND", {fileName: fileName});
			this.showError(title, "");
		});

		this.httpEventService.onDirectoriesNotAllowedError.subscribe((fileName: any) => {
			const title = this.translateService.instant("fileManagement.send.DIRECTORY_NOT_ALLOWED_IN_COMPRESSED", {fileName: fileName});
			this.showError(title, "");
		});

		this.httpEventService.onFileNameInvalidCompressedFileError.subscribe((fileName: any) => {
			const title = this.translateService.instant("fileManagement.send.BAD_FILENAME_IN_COMPRESSED", {fileName: fileName});
			this.showError(title, "");
		});

		this.httpEventService.onMultipleFilesNotAllowedError.subscribe((fileName: any) => {
			const title = this.translateService.instant("fileManagement.send.MULTIPLE_FILES_NOT_ALLOWED_IN_COMPRESSED", {fileName: fileName});
			this.showError(title, "");
		});

		this.httpEventService.onRecipientShortNameNullError.subscribe((fileName: any) => {
			const title = this.translateService.instant("fileManagement.common.RECIPIENT_SHORT_NAME_NULL", {fileName: fileName});
			this.showError(title, "");
		});

		this.httpEventService.onImportedFileIdNotFoundError.subscribe((fileName: any) => {
			const title = this.translateService.instant("fileManagement.send.FILE_ID_NOT_FOUND", {fileName: fileName});
			this.showError(title, "");
		});

		this.httpEventService.onErrorWhileStagingError.subscribe((fileName: any) => {
			const title = this.translateService.instant("fileManagement.send.ERROR_WHILE_STAGING", {fileName: fileName});
			this.showError(title, "");
		});

		this.httpEventService.onInvalidCompressedFileError.subscribe((fileName: any) => {
			const title = this.translateService.instant("fileManagement.send.INVALID_COMPRESSED_FILE", {fileName: fileName});
			this.showError(title, "");
		});
		this.httpEventService.onInvalidFileName.subscribe((fileName: any) => {
			const title = this.translateService.instant("fileManagement.send.INVALID_BATCH_ID", {fileName: fileName});
			this.showError(title, "");
		});

		this.httpEventService.onServiceUnavailableError.subscribe((e: any) => {
			const title = this.translateService.instant("common.SERVICE_ERROR");
			this.showError(title, "");
		});

		this.httpEventService.onInterfaceCreationError.subscribe((e: any) => {
			const title = this.translateService.instant("interfaces.CREATE_INTERFACE_ERROR");
			this.showError(title, "");
		});

		this.httpEventService.onUnauthorizedAccessToken.subscribe((e: any) => {
			const title = this.translateService.instant("tenant.INVALID_ACCESSTOKEN");
			this.showError(title, "");
		});

		this.httpEventService.onPrototypeIdError.subscribe((e: any) => {
			const title = this.translateService.instant("tenant.INVALID_PROTOTYPE_ID");
			this.showError(title, "");
		});

		this.httpEventService.onLxnxPropsInvalidError.subscribe((e: any) => {
			const title = this.translateService.instant("tenant.LXNX_PROPS_INVALID");
			this.showError(title, "");
		});

		this.httpEventService.onLxnxPropsMissingError.subscribe((e: any) => {
			const title = this.translateService.instant("tenant.LXNX_PROPS_MISSING_VALUES");
			this.showError(title, "");
		});

		this.httpEventService.onSupportedSchemaInvalidError.subscribe((e: any) => {
			const title = this.translateService.instant("tenant.SUPPORTED_SCHEMA_INVALID");
			this.showError(title, "");
		});

		this.httpEventService.onSupportedSchemaMissingError.subscribe((e: any) => {
			const title = this.translateService.instant("tenant.SUPPORTED_SCHEMA_MISSING_VALUES");
			this.showError(title, "");
		});

		this.httpEventService.onDuplicateTenantNameError.subscribe((e: any) => {
			const title = this.translateService.instant("tenant.DUPLICATE_TENANT_NAME");
			this.showError(title, "");
		});

		this.httpEventService.onInvalidDataSourceJobPropsError.subscribe((e: any) => {
			const title = this.translateService.instant("tenant.DATA_SOURCE_PROPS_INVALID");
			this.showError(title, "");
		});

		this.httpEventService.onMissingDataSourcePropsError.subscribe((e: any) => {
			const title = this.translateService.instant("tenant.DATA_SOURCE_PROPS_MISSING_VALUES");
			this.showError(title, "");
		});

		this.httpEventService.onClassificationError.subscribe((e: any) => {
			const title = this.translateService.instant("common.CLASSIFICATION_ERROR");
			this.showError(title, "");
		});
		this.httpEventService.onAccessDenied.subscribe((e: any) => {
			const title = this.translateService.instant("common.ACCESS_DENIED");
			this.showError(title, "");
		});
		this.httpEventService.onTenantNameError.subscribe((e: any) => {
			const title = this.translateService.instant("organization.DUPLICATE_TENANT_NAME");
			this.showError(title, "");
		});
		this.httpEventService.onMimeTypeMismatch.subscribe((fileName: any) => {
			const title = this.translateService.instant("common.MIME_TYPE_MISMATCH", {fileName: fileName});
			this.showError(title, "");
		});
		this.httpEventService.onImportFileNotInPendingStatus.subscribe((fileName: any) => {
			const title = this.translateService.instant("fileManagement.send.IMPORT_FILE_NOT_IN_PENDING_STATUS", {fileName: fileName});
			this.showError(title, "");
		});
		this.httpEventService.onFileUploadSystemError.subscribe((fileName: any) => {
			const title = this.translateService.instant("common.SYSTEM_ERROR_FILE_UPLOAD", {fileName: fileName});
			this.showError(title, "");
		});
		this.httpEventService.onSystemError.subscribe(() => {
			const title = this.translateService.instant("common.SYSTEM_ERROR");
			this.showError(title, "");
		});
		this.httpEventService.onZeroByteCompressedFileError.subscribe((fileName: any) => {
			const title = this.translateService.instant("fileManagement.send.ZERO_BYTE_COMPRESSED_FILE_ERROR", {fileName: fileName});
			this.showError(title, "");
		});
		this.httpEventService.onReprocessedFileAlreadyRegisteredError.subscribe((fileName: any) => {
			const title = this.translateService.instant("fileManagement.send.REPROCESS_DUPLICATE_REGISTRATION", {fileName: fileName});
			this.showError(title, "");
		});
		this.httpEventService.onReprocessedDuplicateFileContentError.subscribe((fileName: any) => {
			const title = this.translateService.instant("fileManagement.send.REPROCESS_DUPLICATE_FILE_CONTENT", {fileName: fileName});
			this.showError(title, "");
		});
		this.httpEventService.onKafkaConnectorError.subscribe((errorMessage: any) => {
			this.showError(errorMessage, "");
		});
		this.httpEventService.onFutureForecastModalError.subscribe((e: any) => {
			const title = this.translateService.instant("placementOptimizer.errorDescriptions.FUTURE_FORECAST_MODEL_INVALID", {
				optimizationGroupName: e.optimizationGroupName,
				batchNumber: e.errorbatchCount
			});
			this.showError(title, "");
		});
		this.httpEventService.onPOClientConfigMissing.subscribe((e: any) => {
			const title = this.translateService.instant("tenant.PO_CLIENT_CONFIG_MISSING");
			this.showError(title, "");
		});
		this.httpEventService.onPOClientConfigValueMissing.subscribe((e: any) => {
			const title = this.translateService.instant("tenant.PO_CLIENT_CONFIG_VALUE_MISSING");
			this.showError(title, "");
		});
		this.httpEventService.onInvalidPODataSourcePropsError.subscribe((e: any) => {
			const title = this.translateService.instant("tenant.PO_DATA_SOURCE_PROPS_INVALID");
			this.showError(title, "");
		});
		this.httpEventService.onMissingPODataSourcePropsError.subscribe((e: any) => {
			const title = this.translateService.instant("tenant.PO_DATA_SOURCE_PROPS_MISSING_VALUES");
			this.showError(title, "");
		});
		this.httpEventService.onMissingDownloadRecordDelimiter.subscribe((e: any) => {
			const title = this.translateService.instant("organization.DOWNLOAD_RECORD_DELIMITER_MISSING");
			this.showError(title, "");
		});
		this.httpEventService.onInvalidDownloadRecordDelimiter.subscribe((e: any) => {
			const title = this.translateService.instant("organization.DOWNLOAD_RECORD_DELIMITER_INVALID");
			this.showError(title, "");
		});
		this.httpEventService.onMissingLegacyRMSBankId.subscribe((e: any) => {
			const title = this.translateService.instant("organization.LEGACY_RMS_BANK_ID_MISSING");
			this.showError(title, "");
		});
		this.httpEventService.onInvalidLegacyRMSBankId.subscribe((e: any) => {
			const title = this.translateService.instant("organization.LEGACY_RMS_BANK_ID_INVALID");
			this.showError(title, "");
		});
		this.httpEventService.onDuplicateLegacyRMSBankId.subscribe((e: any) => {
			const title = this.translateService.instant("organization.LEGACY_RMS_BANK_ID_DUPLICATE");
			this.showError(title, "");
		});
		this.httpEventService.onFileFormatInvalid.subscribe((e: any) => {
			const title = this.translateService.instant("organization.FILE_FORMAT_INVALID");
			this.showError(title, "");
		});
		this.httpEventService.onFileFormatMissing.subscribe((e: any) => {
			const title = this.translateService.instant("organization.FILE_FORMAT_MISSING");
			this.showError(title, "");
		});
		this.httpEventService.onMaintenanceRecordLengthMissing.subscribe((e: any) => {
			const title = this.translateService.instant("organization.MAINTENANCE_RECORD_LENGTH_MISSING");
			this.showError(title, "");
		});
		this.httpEventService.onPlacementRecordLengthMissing.subscribe((e: any) => {
			const title = this.translateService.instant("organization.PLACEMENT_RECORD_LENGTH_MISSING");
			this.showError(title, "");
		});
		this.httpEventService.onXMLSchemaVersionMissing.subscribe((e: any) => {
			const title = this.translateService.instant("organization.XML_SCHEMA_VERSION_MISSING");
			this.showError(title, "");
		});
		this.httpEventService.onStoragePropsBucketNameEmpty.subscribe((e: any) => {
			const title = this.translateService.instant("tenant.errors.STORAGE_PROPS_BUCKET_NAME_EMPTY");
			this.showError(title, "");
		});
		this.httpEventService.onStoragePropsObjectKeyPrefixEmpty.subscribe((e: any) => {
			const title = this.translateService.instant("tenant.errors.STORAGE_PROPS_OBJECT_KEY_PREFIX_EMPTY");
			this.showError(title, "");
		});
		this.httpEventService.onStoragePropsRegionEmpty.subscribe((e: any) => {
			const title = this.translateService.instant("tenant.errors.STORAGE_PROPS_REGION_EMPTY");
			this.showError(title, "");
		});
		this.httpEventService.onStoragePropsS3AccessKeyEmpty.subscribe((e: any) => {
			const title = this.translateService.instant("tenant.errors.STORAGE_PROPS_S3ACCESS_KEY_EMPTY");
			this.showError(title, "");
		});
		this.httpEventService.onStoragePropsS3SecretKeyEmpty.subscribe((e: any) => {
			const title = this.translateService.instant("tenant.errors.STORAGE_PROPS_S3SECRET_KEY_EMPTY");
			this.showError(title, "");
		});
		this.httpEventService.onStoragePropsS3ServiceURLEmpty.subscribe((e: any) => {
			const title = this.translateService.instant("tenant.errors.STORAGE_PROPS_S3SERVICE_URL_EMPTY");
			this.showError(title, "");
		});
		this.httpEventService.onConcatenationScheduleInvalid.subscribe((e: any) => {
			const title = this.translateService.instant("organization.concatenationErrors.CONCATENATION_SCHEDULE_INVALID");
			this.showError(title, "");
		});
		this.httpEventService.onConcatenatedFileNameUnmatchedBracesFBFIN.subscribe((e: any) => {
			const title = this.translateService.instant("organization.concatenationErrors.CONCATENATION_FILENAME_UNMATCHED_BRACES_FBFIN");
			this.showError(title, "");
		});
		this.httpEventService.onConcatenatedFileNameUnmatchedBracesFBMNT.subscribe((e: any) => {
			const title = this.translateService.instant("organization.concatenationErrors.CONCATENATION_FILENAME_UNMATCHED_BRACES_FBMNT");
			this.showError(title, "");
		});
		this.httpEventService.onConcatenatedFileNameUnmatchedBracesFBAUD.subscribe((e: any) => {
			const title = this.translateService.instant("organization.concatenationErrors.CONCATENATION_FILENAME_UNMATCHED_BRACES_FBAUD");
			this.showError(title, "");
		});
		this.httpEventService.onConcatenationFileNamesNotUnique.subscribe((e: any) => {
			const title = this.translateService.instant("organization.concatenationErrors.CONCATENATION_FILENAMES_MUST_BE_UNIQUE");
			this.showError(title, "");
		});
		this.httpEventService.onDPMProtoTypeNotDefinedError.subscribe((e: any) => {
			const title = this.translateService.instant("organization.DMP_TENANT_PROTOTYPE_NOT_DEFINED");
			this.showError(title, "");
		});
		this.httpEventService.onDataRetentionPeriodInvalid.subscribe((e: any) => {
			const title = this.translateService.instant("organization.DATA_RETENTION_PERIOD_INVALID");
			this.showError(title, "");
		});
		this.httpEventService.onDataRetentionPeriodMissing.subscribe((e: any) => {
			const title = this.translateService.instant("organization.DATA_RETENTION_PERIOD_MISSING");
			this.showError(title, "");
		});
		this.httpEventService.onFormatBFileSizeError.subscribe((errorMessage: any) => {
			this.showError(errorMessage, "");
		});
		this.httpEventService.onDuplicateBatchId.subscribe((e: any) => {
			const title = this.translateService.instant("fileManagement.send.DUPLICATE_BATCH_ID");
			this.showError(title, "");
		});
		this.httpEventService.onFileNotFoundError.subscribe((fileName: any) => {
			const title = this.translateService.instant("fileManagement.send.FILE_ID_NOT_FOUND", {fileName: fileName});
			this.showError(title, "");
		});
		this.httpEventService.onPassthroughFilesDisabled.subscribe((creditorName: string) => {
			const title = this.translateService.instant("fileManagement.send.PASSTHROUGH_FILE_DISABLED_FOR_CREDITOR", {creditorName: creditorName});
			this.showError(title, "");
		});
		this.httpEventService.onInactiveClientError.subscribe((clientName: any) => {
			const title = this.translateService.instant("fileManagement.common.INACTIVE_USER", {clientName: clientName});
			this.showError(title, "");
		});
		this.httpEventService.onInterfaceCodeExistInSameRelationshipError.subscribe((e: any) => {
			const title = this.translateService.instant("interfaces.INTERFACE_CODE_EXISTS_SAME_RELATIONSHIP");
			this.showError(title, "");
		});

		this.httpEventService.onTransactionAlreadyExistError.subscribe((e: any) => {
			const title = this.translateService.instant("billing.transactionCodeAction.TRANSACTION_CODE_ALREADY_EXISTS");
			this.showError(title, "");
		});

		this.httpEventService.onFileDataAlreadyExistError.subscribe((e: any) => {
			const title = this.translateService.instant("billing.transactionCodeAction.FILEDATA_ALREADY_EXISTS");
			this.showError(title, "");
		});

		this.httpEventService.onDuplicateRateTier.subscribe((e: any) => {
			const title = this.translateService.instant("billing.rateCodesAction.DUPLICATE_BILLINGRATETIER");
			this.showError(title, "");
		});
		this.httpEventService.onDuplicateRateCode.subscribe((e: any) => {
			const title = this.translateService.instant("billing.rateCodesAction.DUPLICATE_BILLINGRATE");
			this.showError(title, "");
		});
		this.httpEventService.onInvalidIPAddressRange.subscribe((errorMessage: string) => {
			let indexValue = errorMessage.indexOf(":");
			errorMessage = errorMessage.substring(indexValue + 1);
			const title = this.translateService.instant("partnerRelationships.IPV4_ADDRESS_RANGE_INVALID", {errorMessage: errorMessage});
			this.showError(title, "");
		});
	}
}
